import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import { particlesConfig } from "./particles-config";
import styled from 'styled-components';
import { motion, useScroll, useTransform, useReducedMotion, useMotionValue, useSpring } from 'framer-motion';
import { Navigation } from './components/Navigation';
import { About } from './pages/About';
import { Community } from './pages/Community';
import spaceImg from './assets/space1.jpg';
import shipImg from './assets/ship.jpg';
import { colors, fonts } from './theme';
import { Eyebrow, StatusDot, GridOverlay, StarFlareIcon, ApplyButton } from './components/HudFrame';

const AppContainer = styled.div`
  min-height: 100vh;
  background: ${colors.bgDeep};
  position: relative;
  overflow: hidden;
`;

const ContentContainer = styled.div`
  position: relative;
  z-index: 1;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HomeContainer = styled.main`
  flex-shrink: 0;
  position: relative;
  display: flex;
  align-items: flex-end;
  min-height: calc(100vh - 68px);
  overflow: hidden;
  padding: 0 4rem 4.5rem;

  @media (max-width: 768px) {
    padding: 0 1.5rem 3rem;
    align-items: flex-end;
  }
`;

// Background image lives in its own layer, separate from the static gradient
// scrim, so scroll-driven parallax (a slow upward drift + gentle scale) can
// be applied to the art alone without disturbing the legibility gradient or
// the text above it.
const HeroImageLayer = styled(motion.div)`
  position: absolute;
  inset: -22% 0 0 0;
  background-image: url(${spaceImg});
  background-size: cover;
  background-position: center 38%;
  will-change: transform;

  @media (max-width: 768px) {
    background-position: center 30%;
  }
`;

const HeroScrim = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(180deg, rgba(5,7,13,0.15) 0%, rgba(5,7,13,0.4) 42%, rgba(5,7,13,0.97) 94%),
    linear-gradient(90deg, rgba(5,7,13,0.88) 0%, rgba(5,7,13,0.22) 55%, rgba(5,7,13,0.08) 100%);
  pointer-events: none;
`;

const ShipEmblem = styled(motion.img)`
  position: absolute;
  top: 12%;
  right: 9%;
  width: 84px;
  height: 84px;
  object-fit: contain;
  filter: drop-shadow(0 0 22px rgba(242, 169, 59, 0.45)) drop-shadow(0 0 46px rgba(167, 139, 250, 0.2));
  pointer-events: none;
  opacity: 0.92;

  @media (max-width: 900px) {
    width: 56px;
    height: 56px;
    top: 8%;
    right: 6%;
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

// Particles render as a global overlay ABOVE the nav/content/section-backdrop
// layers (not behind them) so the twinkling starfield and its mouse-repulse
// interactivity stay visible on every route instead of being painted over by
// each page's opaque hero/section background image. mix-blend-mode: screen
// means dark pixels of the canvas contribute nothing and only the bright
// particle dots actually lighten what's beneath them, so it reads as drifting
// dust/sparkle over the art rather than a wash — legibility of text under it
// is preserved because screen blending can only brighten, never darken or
// obscure. pointer-events: none keeps clicks/hover passing through to the
// real UI; tsparticles' own hover-repulse still works because that listens
// on `window` (interactivity.detectsOn: "window"), independent of the DOM
// stacking order used for painting.
const ParticlesContainer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 5;
  pointer-events: none;
  mix-blend-mode: screen;
  opacity: 0.8;

  #tsparticles {
    width: 100%;
    height: 100%;
  }
`;

const HeroContent = styled(motion.div)`
  position: relative;
  z-index: 1;
  color: ${colors.textPrimary};
  max-width: 720px;
  width: 100%;
`;

const Kicker = styled(Eyebrow)`
  font-size: 0.78rem;

  @media (max-width: 768px) {
    font-size: 0.68rem;
  }
`;

// Wrapping element establishes the 3D perspective the headline tilts within;
// kept separate from the headline itself so the tilt transform and the
// headline's own layout (fluid clamp() sizing, gradient-text clipping) don't
// fight over the same style declarations.
const HeadlineWrap = styled(motion.div)`
  perspective: 900px;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    margin-bottom: 0.75rem;
  }
`;

const Headline = styled(motion.h1)`
  font-family: ${fonts.display};
  /* Fluid, dramatically larger sizing: scales with viewport width but is
     clamped so it never overflows or wraps awkwardly at extreme widths. */
  font-size: clamp(3.2rem, 11vw, 10rem);
  margin: 0;
  letter-spacing: 0.02em;
  line-height: 0.95;
  color: ${colors.textPrimary};
  text-shadow: 0 4px 40px rgba(0,0,0,0.7);
  background:
    radial-gradient(
      480px circle at var(--mx, 50%) var(--my, 30%),
      rgba(255, 255, 255, 0.9) 0%,
      ${colors.amber} 28%,
      transparent 60%
    ),
    linear-gradient(120deg, #ffffff 0%, ${colors.textPrimary} 45%, ${colors.amber} 100%);
  background-blend-mode: overlay, normal;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  transform-style: preserve-3d;
  cursor: default;
  will-change: transform;

  @media (max-width: 768px) {
    letter-spacing: 0.01em;
  }
`;

const Subhead = styled.p`
  font-family: ${fonts.mono};
  font-size: 1.05rem;
  line-height: 1.6;
  color: ${colors.textDim};
  max-width: 560px;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

// Single row holding the Interest Form CTA and the social icons together —
// wraps to a second line on narrow widths (via flex-wrap) rather than
// overlapping or overflowing, instead of the two previously stacked rows.
const CtaRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 2.25rem;

  @media (max-width: 768px) {
    gap: 1.1rem;
    margin-top: 1.75rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1.75rem;
  font-size: 1rem;
  align-items: center;

  @media (max-width: 768px) {
    gap: 1.25rem;
  }

  a {
    color: ${colors.textPrimary};
    text-decoration: none;
    transition: color 0.3s ease;
    display: flex;
    align-items: center;

    &:hover {
      color: ${colors.amber};
    }
  }
`;

const Tooltip = styled.div`
  position: fixed;
  transform: translate(-50%, -50%);
  color: ${colors.bgDeep};
  padding: 6px 12px;
  pointer-events: none;
  font-family: ${fonts.mono};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  z-index: 100;
  background: ${colors.amber};
  border-radius: 2px;

  @media (max-width: 768px) {
    font-size: 10px;
    padding: 5px 9px;
  }
`;

const DebugStar = styled.div`
  position: fixed;
  width: 16px;
  height: 16px;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 100;
  color: ${colors.cyan};
  opacity: 0.85;
  filter: drop-shadow(0 0 3px rgba(143, 216, 255, 0.5));

  @media (max-width: 768px) {
    width: 12px;
    height: 12px;
  }

  svg {
    width: 100%;
    height: 100%;
  }

  @keyframes pulse {
    0%, 100% { transform: translate(-50%, -50%) scale(1); filter: drop-shadow(0 0 3px rgba(143, 216, 255, 0.5)); }
    50% { transform: translate(-50%, -50%) scale(1.08); filter: drop-shadow(0 0 5px rgba(143, 216, 255, 0.75)); }
  }

  animation: pulse 4s ease-in-out infinite;
`;

// Slim amber telemetry line at the very top of the viewport, filling
// left-to-right with document scroll progress — a small nod to the mission
// control "instrument readout" idiom without adding any new chrome.
const ScrollProgressTrack = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  z-index: 50;
  transform-origin: 0% 50%;
  background: linear-gradient(90deg, ${colors.amber}, ${colors.coral});
  box-shadow: 0 0 6px ${colors.amberDim};
`;

const Footer = styled.footer`
  position: relative;
  z-index: 1;
  border-top: 1px solid ${colors.hairline};
  padding: 1.25rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: ${fonts.mono};
  font-size: 0.68rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${colors.textFaint};

  @media (max-width: 768px) {
    padding: 1rem 1.25rem;
    flex-direction: column;
    gap: 0.4rem;
    text-align: center;
  }
`;

// Wraps About/Community so each has a stable anchor id for the nav's
// smooth-scroll links. scroll-margin-top matches the sticky nav's height
// (Navigation's Bar) so anchored scrolling doesn't tuck the section's own
// heading under the bar.
const SectionAnchor = styled.div`
  scroll-margin-top: 68px;
`;

function Home() {
  const prefersReducedMotion = useReducedMotion();
  const heroRef = useRef<HTMLDivElement>(null);

  // Track scroll progress of the hero itself (not the whole document) so the
  // parallax responds as soon as the user starts scrolling this section,
  // rather than being tied to total page length.
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const imageY = useTransform(scrollYProgress, [0, 1], prefersReducedMotion ? ["0%", "0%"] : ["0%", "18%"]);
  const imageScale = useTransform(scrollYProgress, [0, 1], prefersReducedMotion ? [1, 1] : [1, 1.12]);
  const shipY = useTransform(scrollYProgress, [0, 1], prefersReducedMotion ? [0, 0] : [0, 60]);
  const shipOpacity = useTransform(scrollYProgress, [0, 0.8], [0.92, 0]);

  // Headline 3D tilt + cursor-highlight. Deliberately framer-motion
  // useMotionValue instead of React useState: motion values write straight
  // to the DOM style on every mousemove without going through React's
  // render cycle, so hovering the headline never triggers a re-render (the
  // codebase's existing App-level mousemove handler below uses useState and
  // re-renders on every event — fine for its 9 sparse debug-star proximity
  // checks, but not a pattern worth repeating for a continuous, high
  // frequency hover effect on the hero's biggest element).
  const headlineRef = useRef<HTMLDivElement>(null);
  const rotateXRaw = useMotionValue(0);
  const rotateYRaw = useMotionValue(0);
  const rotateX = useSpring(rotateXRaw, { stiffness: 220, damping: 20, mass: 0.4 });
  const rotateY = useSpring(rotateYRaw, { stiffness: 220, damping: 20, mass: 0.4 });

  const handleHeadlineMouseMove = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (prefersReducedMotion) return;
    const el = headlineRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width;
    const py = (event.clientY - rect.top) / rect.height;
    const offsetX = px - 0.5;
    const offsetY = py - 0.5;

    // Small tilt range — a chrome/rocket-fuselage catch-the-light feel, not
    // a gimmicky full 3D flip.
    rotateYRaw.set(offsetX * 14);
    rotateXRaw.set(offsetY * -14);

    // Cursor-tracked highlight: written directly as CSS custom properties
    // rather than through React state, so this also never triggers a
    // re-render — only the browser's own style/paint pipeline does the work.
    el.style.setProperty('--mx', `${px * 100}%`);
    el.style.setProperty('--my', `${py * 100}%`);
  };

  const handleHeadlineMouseLeave = () => {
    if (prefersReducedMotion) return;
    rotateXRaw.set(0);
    rotateYRaw.set(0);
    const el = headlineRef.current;
    if (el) {
      el.style.setProperty('--mx', '50%');
      el.style.setProperty('--my', '30%');
    }
  };

  return (
    <HomeContainer id="top" ref={heroRef}>
      <HeroImageLayer style={{ y: imageY, scale: imageScale }} />
      <HeroScrim />
      <ShipEmblem
        src={shipImg}
        alt=""
        aria-hidden="true"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 0.92, y: [0, -10, 0] }}
        transition={{ opacity: { duration: 1.4 }, y: { duration: 6, repeat: Infinity, ease: 'easeInOut' } }}
        style={prefersReducedMotion ? undefined : { y: shipY, opacity: shipOpacity }}
      />
      <HeroContent
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9 }}
      >
        <Kicker>
          <StatusDot />
          SAN FRANCISCO — EST. 2013
        </Kicker>
        <HeadlineWrap
          ref={headlineRef}
          onMouseMove={handleHeadlineMouseMove}
          onMouseLeave={handleHeadlineMouseLeave}
        >
          <Headline style={prefersReducedMotion ? undefined : { rotateX, rotateY }}>
            Mission Control
          </Headline>
        </HeadlineWrap>
        <Subhead>
          Self-governed since 2013. Home to the founders who'd rather build than wait.
        </Subhead>
        <CtaRow>
          <ApplyButton
            href="https://docs.google.com/forms/d/e/1FAIpQLScg3rlx0zEP-Fz7aTy-soqKH4oywIDwSokcCa8C-TQkQBXAhg/viewform?usp=sharing&ouid=101033723206810482399"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Mission Control interest form"
          >
            <StarFlareIcon />
            Interest Form
          </ApplyButton>
          <SocialLinks>
            <a href="https://twitter.com/mission__ctrl" target="_blank" rel="noopener noreferrer" aria-label="Mission Control on X">𝕏</a>
            <a href="https://www.businessinsider.com/san-francisco-oldest-hacker-house-startups-2025-4" target="_blank" rel="noopener noreferrer" aria-label="Mission Control on Business Insider">
              <svg width="52" height="16" viewBox="0 0 103 32" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M7.984 32h2.396V19.2H7.984zM16.767 31.808h2.146v-8.076L27.946 32V19.392h-2.14v7.938l-9.039-8.13zM32.845 28.708c1.168.91 2.57 1.436 4.073 1.436 1.464 0 2.491-.504 2.491-1.617 0-.97-.655-1.35-1.802-1.775l-1.857-.645c-1.712-.608-3.014-1.496-3.014-3.537 0-2.183 1.971-3.37 4.328-3.37 1.444 0 2.76.363 3.788 1.006v2.165c-1.069-.79-2.239-1.334-3.742-1.334-1.294 0-2.261.483-2.261 1.49 0 .91.607 1.274 1.676 1.64l2.069.726c2.005.705 2.925 1.798 2.925 3.596 0 2.262-1.99 3.511-4.601 3.511-1.716 0-3.175-.48-4.073-1.086v-2.206zM47.109 32h2.395V19.2h-2.395zM60.74 30.063c2.7 0 4.143-1.732 4.143-4.466 0-2.754-1.444-4.48-4.143-4.48h-2.67v8.946h2.67zM55.89 19.2h4.93c3.795 0 6.249 2.5 6.249 6.397 0 3.9-2.454 6.403-6.249 6.403h-4.93V19.2zM79.047 19.2v1.917H73.25v3.08h5.199v1.895h-5.2v3.971h5.798V32h-7.985V19.2zM87.549 24.996h2.377c1.407 0 2.2-.772 2.2-1.959s-.793-1.96-2.2-1.96h-2.377v3.92zm0 1.81V32h-2.115V19.2h4.532c2.584 0 4.256 1.331 4.256 3.837 0 2.143-.928 3.374-2.905 3.69L95.814 32h-2.406l-4.354-5.193H87.55zM7.147 6.59c.937-.343 1.667-1.372 1.667-2.787 0-2.358-1.667-3.58-4.334-3.58H0v13.163h4.896c2.667 0 4.334-1.308 4.334-3.773 0-1.394-.875-2.616-2.083-3.023zm-5.043-.836V2.153H4.46c1.48 0 2.271.536 2.271 1.779 0 1.243-.792 1.822-2.271 1.822H2.104zm0 1.844h2.75c1.46 0 2.251.728 2.251 1.929 0 1.179-.792 1.93-2.25 1.93h-2.75v-3.86zM19.005 13.6c3.063 0 5.126-1.844 5.126-5.445V.223h-2.167v7.674c0 2.53-1.042 3.71-2.959 3.71s-2.958-1.18-2.958-3.71V.223H13.88v7.91c0 3.623 2.083 5.467 5.125 5.467zm10.228-1.158c.896.644 2.355 1.158 4.063 1.158 2.605 0 4.584-1.33 4.584-3.73 0-1.908-.916-3.066-2.917-3.816l-2.062-.772c-1.063-.385-1.667-.771-1.667-1.736 0-1.072.958-1.586 2.25-1.586 1.5 0 2.667.578 3.73 1.415V1.08C36.193.395 34.88.009 33.442.009c-2.354 0-4.313 1.265-4.313 3.58 0 2.165 1.292 3.108 3 3.751l1.855.686c1.146.45 1.792.858 1.792 1.887 0 1.179-1.021 1.715-2.48 1.715-1.5 0-2.896-.558-4.063-1.522v2.336zM45.623.223h-2.166v13.162h2.166V.223zm6.182 13.146h2.088V4.806l8.788 8.767V.203h-2.082V8.62L51.805 0v13.369zM76.222.226v1.971h-5.626v3.168h5.045v1.949h-5.045v4.083h5.626v1.992h-7.75V.226h7.75zm5.045 12.207c.896.644 2.354 1.158 4.063 1.158 2.604 0 4.584-1.329 4.584-3.73 0-1.908-.917-3.065-2.917-3.816l-2.063-.771c-1.063-.386-1.667-.772-1.667-1.737 0-1.072.958-1.586 2.25-1.586 1.5 0 2.667.579 3.73 1.415V1.072C88.226.386 86.913 0 85.475 0c-2.354 0-4.313 1.265-4.313 3.58 0 2.165 1.292 3.108 3 3.751l1.855.686c1.146.45 1.792.858 1.792 1.887 0 1.179-1.02 1.715-2.48 1.715-1.5 0-2.896-.557-4.062-1.522v2.336zm13.086 0c.896.644 2.355 1.158 4.063 1.158 2.605 0 4.584-1.329 4.584-3.73 0-1.908-.917-3.065-2.917-3.816l-2.063-.771c-1.062-.386-1.667-.772-1.667-1.737 0-1.072.959-1.586 2.25-1.586 1.5 0 2.668.579 3.73 1.415V1.072C101.313.386 100 0 98.562 0c-2.355 0-4.313 1.265-4.313 3.58 0 2.165 1.292 3.108 3 3.751l1.855.686c1.146.45 1.792.858 1.792 1.887 0 1.179-1.021 1.715-2.48 1.715-1.5 0-2.896-.557-4.063-1.522v2.336z" />
              </svg>
            </a>
          </SocialLinks>
        </CtaRow>
      </HeroContent>
    </HomeContainer>
  );
}

function App() {
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);

  // Single continuous page now (no router) — one document title for the
  // whole thing, set once here instead of per-section.
  useEffect(() => {
    document.title = 'Mission Control — San Francisco\'s Oldest Hacker House';
  }, []);

  const { scrollYProgress } = useScroll();

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipText, setTooltipText] = useState("");
  const [tooltipParticles] = useState([
    { x: 0.2, y: 0.2, text: "HF0" },
    { x: 0.4, y: 0.3, text: "Pylon" },
    { x: 0.6, y: 0.2, text: "Scale" },
    { x: 0.8, y: 0.3, text: "Hightouch" },
    { x: 0.3, y: 0.5, text: "Feathery" },
    { x: 0.7, y: 0.5, text: "TrueWind" },
    { x: 0.2, y: 0.7, text: "Italic" },
    { x: 0.5, y: 0.8, text: "Pallet" },
    { x: 0.8, y: 0.7, text: "Workflow" },
    { x: 0.5, y: 0.15, text: "Hydra DB" },
    { x: 0.15, y: 0.45, text: "Hyperspell" },
    { x: 0.9, y: 0.5, text: "Superpower" },
    { x: 0.35, y: 0.85, text: "New Lantern" },
    { x: 0.65, y: 0.85, text: "Apple Shortcuts" },
    { x: 0.1, y: 0.85, text: "Jam.dev" }
  ]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const x = event.clientX / window.innerWidth;
      const y = event.clientY / window.innerHeight;
      setMousePos({ x, y });

      // Check if mouse is near any tooltip particle
      const isNearParticle = tooltipParticles.some(particle => {
        const distance = Math.sqrt(
          Math.pow((x - particle.x), 2) + Math.pow((y - particle.y), 2)
        );
        if (distance < 0.015) { // 1.5% of screen size as threshold
          setTooltipText(particle.text);
          return true;
        }
        return false;
      });

      setShowTooltip(isNearParticle);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [tooltipParticles]);

  useEffect(() => {
    initParticlesEngine(async (engine) => {
      await loadSlim(engine);
      const baseConfig = particlesConfig as Required<typeof particlesConfig>;
      const groups = baseConfig.particles?.groups ?? {
        tooltip: {
          size: { value: { min: 3, max: 5 } },
          color: { value: "#f2a93b" },
          twinkle: {
            particles: {
              enable: true,
              color: "#f2a93b",
              frequency: 0.4,
              opacity: 1
            }
          },
          number: { value: 3 }
        },
        regular: {
          size: { value: { min: 2, max: 4 } },
          color: { value: ["#eef2f7", "#8fd8ff"] },
          twinkle: {
            particles: {
              enable: true,
              color: "#8fd8ff",
              frequency: 0.1,
              opacity: 0.8
            }
          },
          number: { value: 50 }
        }
      };

      await engine.load({
        id: "tsparticles",
        options: {
          ...baseConfig,
          particles: {
            ...baseConfig.particles,
            groups: {
              ...groups,
              tooltip: {
                ...groups.tooltip,
                options: {
                  position: tooltipParticles.map(p => ({
                    x: p.x * 100,
                    y: p.y * 100
                  }))
                }
              }
            }
          }
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppContainer>
      <ScrollProgressTrack style={{ scaleX: scrollYProgress }} />
      <ParticlesContainer>
        <div id="tsparticles" />
        <GridOverlay />
      </ParticlesContainer>

      <ContentContainer>
        <Navigation />
        <Home />
        <SectionAnchor id="about">
          <About />
        </SectionAnchor>
        <SectionAnchor id="community">
          <Community />
        </SectionAnchor>
        <Footer>
          <span>MISSION CONTROL — SAN FRANCISCO, CA — MISSION DISTRICT</span>
          <span>EST. 2013</span>
        </Footer>
      </ContentContainer>

      {/* Star-flare markers over company constellation points */}
      {tooltipParticles.map((particle, index) => (
        <DebugStar
          key={index}
          style={{
            left: particle.x * window.innerWidth,
            top: particle.y * window.innerHeight,
            animationDelay: `${index * 0.3}s`
          }}
        >
          <StarFlareIcon style={{ width: '100%', height: '100%' }} />
        </DebugStar>
      ))}

      {showTooltip && (
        <Tooltip
          style={{
            left: (mousePos.x * window.innerWidth) - 2,
            top: mousePos.y * window.innerHeight,
            transform: 'translate(-100%, -50%)'
          }}
        >
          {tooltipText}
        </Tooltip>
      )}
    </AppContainer>
  );
}

export default App;
